import { Body, Button, Caption, InputGroup, Popover } from "@pej/kore/components";
import { Search, Upload, X } from "lucide-react";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { AssetDeleteConfirm } from "./asset-delete-confirm";
import { ASSET_KIND } from "./asset-kind";
import { AssetTileGrid } from "./asset-tile-grid";
import type {
    AssetFolderSummary,
    AssetLoader,
    AssetSummary,
    AssetType,
    CreateFolderResult,
    FolderLoader,
    FolderScopeValue,
    NameResult,
} from "./asset-types";
import { FolderDeleteConfirm } from "./folder-delete-confirm";
import { FolderMenu } from "./folder-menu";
import { FolderScope } from "./folder-scope";
import { RenamePopover } from "./rename-popover";

export interface AssetPickerProps {
    /** The header heading, and the picker's accessible name — per-slot ("Choose a
     *  background" / "Choose a logo"), so the host supplies it rather than the
     *  picker guessing from context it doesn't have. */
    title: string;
    /** The one kind of file this picker lists, uploads and talks about. The
     *  host's loader applies the matching search filter. */
    assetType: AssetType;
    /** Must not reject — same contract as the callbacks documented below. */
    loadAssets: AssetLoader;
    /** Must not reject — see `loadAssets` above. */
    loadFolders: FolderLoader;
    canManage: boolean;
    quota: { used: number; limit: number };
    /** The asset currently referenced by the field this picker was opened from, if any. */
    inUseAssetId: string | null;
    onSelect: (asset: AssetSummary) => void;
    onPreview: (asset: AssetSummary | null) => void;
    /** Marks assets too small to print sharply in this slot — the grid shows a
     *  caption warning + tooltip. Passed in so the picker stays free of the
     *  print-resolution rules (which are the field's to know). */
    lowRes?: (asset: AssetSummary) => boolean;
    onUpload: (files: FileList, folderId: string | null) => void;
    /**
     * None of these — nor the two loaders above — may reject. This is a
     * presentational family — it owns no error UI — so a failure has to be
     * handled by the host: catch inside the callback (or loader) and report
     * it there. The three callbacks that can fail on a name the service
     * refuses — `onRenameAsset`, `onRenameFolder`, `onCreateFolder` —
     * resolve with a result instead, which the picker renders inline (spec
     * §5.5); the rest (`onMoveAsset`, `onDeleteAsset`, `onDeleteFolder`, and
     * both loaders) resolve on success and are the picker's only signal to
     * update what it shows. A rejection would surface as an unhandled
     * rejection with no visible change, which is worse than any host-side
     * handling would be. The picker adds a defensive `.catch` at the two
     * locations where a loader rejecting would otherwise strand open UI (I6,
     * final review) — that is a backstop, not the contract.
     */
    onMoveAsset: (assetId: string, folderId: string | null) => Promise<void>;
    onRenameAsset: (assetId: string, name: string) => Promise<NameResult>;
    onDeleteAsset: (asset: AssetSummary) => Promise<void>;
    onCreateFolder: (name: string) => Promise<CreateFolderResult>;
    onRenameFolder: (folderId: string, name: string) => Promise<NameResult>;
    onDeleteFolder: (folderId: string) => Promise<void>;
    onClose: () => void;
}

// 560px, 4 columns (spec §B1) — up from the original 420px to make room for
// the caption row below each tile. Which assets land above the fold is what
// `sort: "newest"` decides.
const COLUMNS = 4;
const SEARCH_DEBOUNCE_MS = 200;

function useDebouncedValue<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(timer);
    }, [value, delayMs]);
    return debounced;
}

/**
 * The picker's content — header, search, folder scope, grid, footer — built
 * to be dropped into an anchored popover, not a dialog (a dialog was built,
 * measured against this, and rejected). This component makes no assumption
 * about what hosts it: it owns no `open` state and renders unconditionally,
 * so the host decides when it's mounted (typically a kore `Popover.Content`).
 *
 * Layout is a flex column where only the grid scrolls — header, search, the
 * scope row and the footer stay pinned so no control is ever scrolled out of
 * reach; the popover sizes to the space below its anchor and the grid alone
 * absorbs the loss.
 */
export function AssetPicker({
    title,
    assetType,
    loadAssets,
    loadFolders,
    canManage,
    quota,
    inUseAssetId,
    onSelect,
    onPreview,
    lowRes,
    onUpload,
    onMoveAsset,
    onRenameAsset,
    onDeleteAsset,
    onCreateFolder,
    onRenameFolder,
    onDeleteFolder,
    onClose,
}: AssetPickerProps) {
    const [searchText, setSearchText] = useState("");
    const debouncedText = useDebouncedValue(searchText, SEARCH_DEBOUNCE_MS);

    const [folders, setFolders] = useState<AssetFolderSummary[]>([]);
    const [scope, setScope] = useState<FolderScopeValue>({ mode: "all" });

    const [assets, setAssets] = useState<AssetSummary[]>([]);
    const [nextPageToken, setNextPageToken] = useState<string | null>(null);
    const [loadingMore, setLoadingMore] = useState(false);

    const [pendingDelete, setPendingDelete] = useState<AssetSummary | null>(null);
    const [deleting, setDeleting] = useState(false);

    const [folderMenuOpen, setFolderMenuOpen] = useState(false);
    const [renamingFolder, setRenamingFolder] = useState<AssetFolderSummary | null>(null);
    const [deletingFolder, setDeletingFolder] = useState<AssetFolderSummary | null>(null);
    const [folderAssetCount, setFolderAssetCount] = useState<number | null>(null);
    const [deletingFolderPending, setDeletingFolderPending] = useState(false);
    // Bumped whenever a mutation could have changed what the current query
    // matches. Moving an asset under a folder scope leaves it sitting in a list
    // it no longer belongs to; renaming changes what a search matches.
    const [revision, setRevision] = useState(0);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const gridWrapperRef = useRef<HTMLDivElement>(null);
    // Anchor for the folder menu popover — the whole scope row, not the
    // overflow chip that opens it. The trigger lives inside FolderScope, a
    // separate component, so `Popover.Trigger` can't wrap it directly; the
    // popover anchors to a container both sit inside instead.
    const scopeRowRef = useRef<HTMLDivElement>(null);
    // Whether the operator's focus was inside the grid right before a requery
    // landed. A search/scope change replaces `assets` wholesale, which unmounts
    // the tile that had focus — the browser drops focus to `document.body`
    // rather than moving it, so the effect below has to put it back itself.
    const hadGridFocusRef = useRef(false);
    // `loadAssets` is read through a ref, same reasoning as `loadFolders`
    // below but made explicit here because the consequence is worse: the
    // picker calls `onPreview` on every tile hover and focus, and a host
    // holding preview state (the point of live preview) re-renders on each
    // one. An inline `loadAssets={(q) => client.search(q)}` — not wrapped in
    // `useCallback` — would get a fresh identity every one of those renders;
    // if the query effect depended on it directly, every hover would fire a
    // fresh page-one query. Refreshing the ref every render and never putting
    // `loadAssets` itself in the effect's deps means the query only re-fires
    // when what it actually asks for changes.
    const loadAssetsRef = useRef(loadAssets);
    loadAssetsRef.current = loadAssets;
    // Read through a ref so the cleanup does not re-run — and re-fire — every
    // time the host passes a fresh onPreview identity, which it does on every
    // render it makes in response to the previous signal.
    const onPreviewRef = useRef(onPreview);
    onPreviewRef.current = onPreview;

    // The tree loads once. It only changes shape from create (see
    // handleCreateFolder, which re-fetches explicitly) — a search or scope
    // change must not refetch folders it never affected.
    // biome-ignore lint/correctness/useExhaustiveDependencies: loadFolders is treated as stable for the picker's lifetime, like loadAssets above
    useEffect(() => {
        let cancelled = false;
        loadFolders().then((result) => {
            if (!cancelled) setFolders(result);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    // The query that actually drives the grid: on mount, and again whenever
    // text (debounced) or the scope changes — or `revision` bumps, meaning a
    // move/rename/delete could have changed what the current query matches.
    // Never keyed on `assets`/`nextPageToken` themselves, or a "load more"
    // response would loop back into a full requery of page one. Not keyed on
    // `loadAssets` either — see `loadAssetsRef` above.
    // biome-ignore lint/correctness/useExhaustiveDependencies: revision is a trigger-only counter, not read by the effect body — it exists solely to force this requery
    useEffect(() => {
        let cancelled = false;
        hadGridFocusRef.current = !!gridWrapperRef.current?.contains(document.activeElement);
        loadAssetsRef.current({ text: debouncedText, scope, pageToken: null, sort: "newest" }).then((page) => {
            if (cancelled) return;
            setAssets(page.items);
            setNextPageToken(page.nextPageToken);
        });
        return () => {
            cancelled = true;
        };
    }, [debouncedText, scope, revision]);

    // Restores focus into the grid after a requery replaces the tile that had
    // it. `AssetTileGrid` always clamps exactly one option to `tabIndex={0}`
    // once the new list renders, so that's the one query away — this can't
    // reach into the grid's own internal state, only its committed DOM.
    // biome-ignore lint/correctness/useExhaustiveDependencies: assets drives when to re-check focus, not what the effect body reads
    useEffect(() => {
        if (!hadGridFocusRef.current) return;
        hadGridFocusRef.current = false;
        if (document.activeElement !== document.body) return;
        gridWrapperRef.current?.querySelector<HTMLElement>('[role="option"][tabindex="0"]')?.focus();
    }, [assets]);

    // Escape closes the innermost surface first (spec §14). Every layer below
    // owns its own Escape handling; this listener only ever closes the picker,
    // and only once none of them is open — defensively, in case any of them
    // does not stop the key from bubbling. One press is never ambiguous.
    useEffect(() => {
        function handleKeyDown(event: KeyboardEvent) {
            if (event.key !== "Escape") return;
            if (pendingDelete || deletingFolder || renamingFolder || folderMenuOpen) return;
            onClose();
        }
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [pendingDelete, deletingFolder, renamingFolder, folderMenuOpen, onClose]);

    // The picker unmounting is the operator no longer considering anything. The
    // host cannot infer that: it sees a preview signal and then silence.
    useEffect(() => {
        return () => onPreviewRef.current(null);
    }, []);

    // Upload where you are, organize later (spec §B7): the target is read
    // from the current scope at selection time, not chosen up front — there
    // is no separate "upload to folder" step for the native file dialog to
    // carry.
    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        event.target.value = "";
        if (!files || files.length === 0) return;
        onUpload(files, scope.mode === "folder" ? scope.folderId : null);
        onClose();
    };

    const refreshFolders = async () => setFolders(await loadFolders());

    const handleCreateFolder = async (name: string): Promise<CreateFolderResult> => {
        const result = await onCreateFolder(name);
        if (result.ok) await refreshFolders();
        return result;
    };

    const requestFolderDelete = async (folder: AssetFolderSummary) => {
        setDeletingFolder(folder);
        setFolderAssetCount(null);
        // The count comes from the search response the picker already knows how
        // to ask for. A dedicated count callback would widen the contract to
        // fetch a number `totalCount` already carries.
        // `loadAssets` must not reject (see the docblock above), but a
        // rejection here would otherwise leave `folderAssetCount` null
        // forever, stranding the confirm's Delete button disabled behind
        // "Counting…" (I6, final review) — this is the backstop, not a retry.
        const page = await loadAssetsRef
            .current({
                text: "",
                scope: { mode: "folder", folderId: folder.id },
                pageToken: null,
                sort: "newest",
            })
            .catch(() => null);
        if (page) setFolderAssetCount(page.totalCount);
    };

    const confirmFolderDelete = async () => {
        if (!deletingFolder) return;
        const folder = deletingFolder;
        setDeletingFolderPending(true);
        try {
            await onDeleteFolder(folder.id);
            // In the same interaction, or the scope points at something that no
            // longer exists and the grid stays empty forever.
            if (scope.mode === "folder" && scope.folderId === folder.id) setScope({ mode: "all" });
            // A rejecting refetch must not strand this confirmation open after
            // the folder was already deleted server-side (I6, final review):
            // best-effort here means the folder tree may show stale data
            // until the next successful fetch, but the confirmation still
            // closes and the grid still requeries below.
            await refreshFolders().catch(() => undefined);
            setRevision((n) => n + 1);
            setDeletingFolder(null);
        } finally {
            setDeletingFolderPending(false);
        }
    };

    const handleMoveAsset = async (asset: AssetSummary, folderId: string | null) => {
        await onMoveAsset(asset.id, folderId);
        setRevision((n) => n + 1);
    };

    const handleRenameAsset = async (asset: AssetSummary, name: string) => {
        const result = await onRenameAsset(asset.id, name);
        if (result.ok) setRevision((n) => n + 1);
        return result;
    };

    const handleRenameFolder = async (name: string) => {
        if (!renamingFolder) return { ok: false as const, message: "No folder is being renamed." };
        const result = await onRenameFolder(renamingFolder.id, name);
        if (result.ok) await refreshFolders();
        return result;
    };

    const handleConfirmDelete = async () => {
        if (!pendingDelete) return;
        const asset = pendingDelete;
        setDeleting(true);
        try {
            await onDeleteAsset(asset);
            setAssets((current) => current.filter((item) => item.id !== asset.id));
            setPendingDelete(null);
            setRevision((n) => n + 1);
        } finally {
            setDeleting(false);
        }
    };

    const handleLoadMore = async () => {
        if (!nextPageToken || loadingMore) return;
        setLoadingMore(true);
        try {
            // Reads `loadAssets` directly, not `loadAssetsRef` — safe here
            // because this only ever runs from a click, which (unlike the
            // query effect above) has no stale-closure exposure to guard
            // against; the ref discipline exists for effects (M6, final
            // review).
            const page = await loadAssets({
                text: debouncedText,
                scope,
                pageToken: nextPageToken,
                sort: "newest",
            });
            setAssets((current) => [...current, ...page.items]);
            setNextPageToken(page.nextPageToken);
        } finally {
            setLoadingMore(false);
        }
    };

    return (
        // biome-ignore lint/a11y/useSemanticElements: <fieldset> is for grouping form controls and carries browser-default border/margin chrome this panel can't use; this groups the whole picker (search, folders, grid, footer), not form inputs, so div+role="group" is the correct pattern (see folder-chip.tsx for the same call).
        <div
            role="group"
            aria-label={title}
            className="flex h-[30rem] w-[35rem] flex-col overflow-hidden rounded-kore-md border border-kore-border bg-kore-surface"
        >
            {/* Backs the header's "Upload file" button. The grid's own upload
                tile (a second trigger for the same dialog) was measured against
                the 234px grid and dropped — it cost one of eight visible slots
                to duplicate this control a few pixels above it (spec §13). */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ASSET_KIND[assetType].accept}
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
                onChange={handleFileChange}
            />

            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-kore-border p-3">
                {/* A bare `<h2>` wrapping `Body`, not `Body as="h2"` — kore's Body
                    only renders `span`/`p` (its Text primitives own that
                    contract, not the picker's to widen). The picker still needs
                    a real heading here: this is a plain popover panel with no
                    kore `Popover.Title` of its own (that would require a
                    `Popover.Root` ancestor, which this component — hostable
                    outside a popover, per the docblock above — cannot assume). */}
                <h2>
                    <Body className="font-medium">{title}</Body>
                </h2>
                <div className="flex items-center gap-1.5">
                    {/* Upload is a manage-only affordance — absent, not disabled. */}
                    {canManage && (
                        <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                            <Upload aria-hidden="true" />
                            Upload file
                        </Button>
                    )}
                    <Button type="button" variant="ghost" size="icon-sm" aria-label="Close" onClick={onClose}>
                        <X aria-hidden="true" />
                    </Button>
                </div>
            </div>

            <div className="shrink-0 border-b border-kore-border p-3">
                <InputGroup>
                    <InputGroup.Prefix>
                        <Search aria-hidden="true" className="size-3.5 text-kore-text-muted" />
                    </InputGroup.Prefix>
                    <InputGroup.Input
                        type="search"
                        aria-label={ASSET_KIND[assetType].searchLabel}
                        placeholder={ASSET_KIND[assetType].searchPlaceholder}
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                    />
                </InputGroup>
            </div>

            <div ref={scopeRowRef} className="flex shrink-0 items-center gap-1.5 border-b border-kore-border p-3">
                <div className="min-w-0 flex-1">
                    <FolderScope
                        folders={folders}
                        scope={scope}
                        canManage={canManage}
                        onScopeChange={setScope}
                        onOpenFolderMenu={() => setFolderMenuOpen(true)}
                        onRenameFolder={setRenamingFolder}
                        onDeleteFolder={(folder) => void requestFolderDelete(folder)}
                    />
                </div>
                <Popover open={folderMenuOpen} onOpenChange={setFolderMenuOpen}>
                    <Popover.Content anchor={scopeRowRef} align="end">
                        <FolderMenu
                            folders={folders}
                            scope={scope}
                            canManage={canManage}
                            onScopeChange={setScope}
                            onCreateFolder={handleCreateFolder}
                            onClose={() => setFolderMenuOpen(false)}
                        />
                    </Popover.Content>
                </Popover>
            </div>

            <div
                ref={gridWrapperRef}
                data-testid="asset-grid-scroll"
                className="min-h-0 flex-1 overflow-y-auto p-3"
                onScroll={() => onPreview(null)}
            >
                <AssetTileGrid
                    assets={assets}
                    columns={COLUMNS}
                    canManage={canManage}
                    folders={folders}
                    onSelect={(asset) => {
                        onSelect(asset);
                        onClose();
                    }}
                    onPreview={onPreview}
                    // Go through the revision-bumping wrappers rather than
                    // straight to the host callbacks: a move or rename can
                    // change what the current query matches, so the grid must
                    // requery afterward instead of trusting stale results.
                    onMove={handleMoveAsset}
                    onRenameAsset={handleRenameAsset}
                    onCreateFolder={handleCreateFolder}
                    onDeleteRequest={(asset) => setPendingDelete(asset)}
                    inUseAssetId={inUseAssetId}
                    lowRes={lowRes}
                />
                {nextPageToken && (
                    <div className="mt-3 flex justify-center">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                        >
                            {loadingMore ? "Loading…" : "Load more"}
                        </Button>
                    </div>
                )}
            </div>

            <div className="flex shrink-0 items-center justify-between gap-2 border-t border-kore-border p-3">
                <Caption className="text-kore-text-muted">
                    {/* "files", not the picker's noun: the ceiling counts images and
                        fonts together. */}
                    {quota.used} of {quota.limit} files used
                </Caption>
                {/* No hover hint: in vouchers the hovered image previewed on the
                    ticket, and every host here passes an `onPreview` that does
                    nothing — so there is nothing to point the reader at. */}
            </div>

            <AssetDeleteConfirm
                asset={pendingDelete}
                usedByThisTemplate={pendingDelete?.id === inUseAssetId}
                quotaLimit={quota.limit}
                isPending={deleting}
                onConfirm={handleConfirmDelete}
                onCancel={() => setPendingDelete(null)}
            />

            {/* Anchored to the scope row, not to the chip that opened it: chips
                come and go as the row re-fits under `FolderScope`'s own
                measurement, and a popover anchored to a chip that overflows
                away mid-edit would be orphaned with no element to anchor to. */}
            <RenamePopover
                open={renamingFolder !== null}
                title="Rename folder"
                initialValue={renamingFolder?.name ?? ""}
                anchor={scopeRowRef}
                onSubmit={handleRenameFolder}
                onOpenChange={(open) => {
                    if (!open) setRenamingFolder(null);
                }}
            />

            <FolderDeleteConfirm
                folder={deletingFolder}
                assetCount={folderAssetCount}
                isPending={deletingFolderPending}
                onConfirm={() => void confirmFolderDelete()}
                onCancel={() => setDeletingFolder(null)}
            />
        </div>
    );
}
